import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { MessageCircle, X, Send, Phone, Mail, LifeBuoy, Loader2, ArrowRight } from "lucide-react";
import { API, track } from "@/lib/api";
import { PREFILL_KEY } from "@/context/BookingContext";
import { useSettings } from "@/context/SettingsContext";
import { useAuth } from "@/context/AuthContext";

const SID_KEY = "kc_chat_session";
const sid = () => { let s = sessionStorage.getItem(SID_KEY); if (!s) { s = (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`); sessionStorage.setItem(SID_KEY, s); } return s; };
const SUGGEST = ["How much is a 3-bedroom cleaning?", "Regular vs deep cleaning?", "Which areas do you serve?", "Help me book a cleaning"];

function Bubble({ m, onPrefill }) {
  const mine = m.role === "user";
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${mine ? "bg-[var(--kc-blue)] text-white rounded-br-md" : "bg-white border border-[#E5E2DA] rounded-bl-md"}`} data-testid={`chat-msg-${m.role}`}>
        {m.content || (m.pending ? <Loader2 className="w-4 h-4 animate-spin text-[var(--kc-blue)]" /> : "")}
        {m.tool && !m.content && <p className="text-xs text-[#5A606A] mt-1">Checking live details…</p>}
        {(m.actions || []).filter((a) => a.type === "prefill_booking").map((a, i) => <button key={i} type="button" onClick={() => onPrefill(a.data)} className="btn-primary h-10 px-4 mt-3 text-xs w-full" data-testid="chat-continue-booking">Continue booking <ArrowRight className="w-3.5 h-3.5" /></button>)}
      </div>
    </div>
  );
}

export function ChatLauncher() {
  const { settings } = useSettings();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef();

  useEffect(() => {
    if (!open) return;
    fetch(`${API}/chat/history/${sid()}`, { credentials: "include", headers: localStorage.getItem("kc_token") ? { Authorization: `Bearer ${localStorage.getItem("kc_token")}` } : {} })
      .then((r) => (r.ok ? r.json() : [])).then((h) => { if (Array.isArray(h) && h.length) setMsgs(h); }).catch(() => {});
  }, [open]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, open]);

  if (!settings?.chat_widget_enabled || pathname.startsWith("/admin")) return null;

  const prefill = (data) => { localStorage.setItem(PREFILL_KEY, JSON.stringify(data)); localStorage.removeItem("kc_booking_draft"); track("ai_booking_assist_started"); setOpen(false); navigate("/book"); window.location.reload(); };

  const send = async (q) => {
    const message = (q ?? text).trim();
    if (!message || busy) return;
    setText(""); setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "", pending: true, actions: [] }]);
    const upd = (fn) => setMsgs((m) => { const c = [...m]; c[c.length - 1] = fn(c[c.length - 1]); return c; });
    try {
      const headers = { "Content-Type": "application/json" };
      const tok = localStorage.getItem("kc_token"); if (tok) headers.Authorization = `Bearer ${tok}`;
      const res = await fetch(`${API}/chat/message`, { method: "POST", credentials: "include", headers, body: JSON.stringify({ session_id: sid(), message }) });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || "unavailable"); }
      const reader = res.body.getReader(); const dec = new TextDecoder(); let buf = "";
      while (true) {
        const { value, done } = await reader.read(); if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n"); buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const ev = JSON.parse(p.slice(6));
          if (ev.t) upd((l) => ({ ...l, content: l.content + ev.t, pending: false }));
          if (ev.tool) upd((l) => ({ ...l, tool: ev.tool }));
          if (ev.actions) upd((l) => ({ ...l, actions: ev.actions, pending: false }));
          if (ev.error) upd((l) => ({ ...l, content: ev.error, pending: false, error: true }));
        }
      }
    } catch (e) {
      upd((l) => ({ ...l, pending: false, error: true, content: e.message === "unavailable" ? "Our assistant is unavailable right now. Please use the contact options below." : String(e.message || "Something went wrong. Please try again.") }));
    } finally { setBusy(false); }
  };

  return (
    <>
      <button type="button" onClick={() => { setOpen(!open); if (!open) track("ai_chat_opened"); }} aria-label={open ? "Close chat" : "Open chat"} data-testid="chat-launcher-button"
        className={`fixed right-4 z-50 rounded-full bg-[var(--kc-blue)] text-white shadow-lg place-items-center hover:bg-[#1D4ED8] transition-[transform,background-color] hover:scale-105 ${pathname.startsWith("/book") ? (open ? "grid" : "hidden md:grid") + " bottom-32" : pathname.startsWith("/account") ? "grid bottom-24" : "grid bottom-20"} h-11 w-11 md:bottom-6 md:h-14 md:w-14`}>
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>
      {open && (
        <div className="fixed z-50 inset-x-3 bottom-[9.5rem] md:inset-x-auto md:right-6 md:bottom-24 md:w-[380px] h-[min(70vh,560px)] rounded-3xl bg-[var(--kc-bg)] border border-[#E5E2DA] shadow-2xl flex flex-col overflow-hidden rise" role="dialog" aria-label="Support chat" data-testid="chat-panel">
          <div className="px-5 py-4 bg-[var(--kc-blue)] text-white flex items-center justify-between">
            <div><p className="font-semibold">{settings.short_name || "Kaur"} Assistant</p><p className="text-xs text-white/75">{user ? `Hi ${user.name?.split(" ")[0]} · answers use your account` : "Instant answers · real prices"}</p></div>
            <button type="button" onClick={() => setOpen(false)} aria-label="Close" className="h-8 w-8 grid place-items-center rounded-full hover:bg-white/10" data-testid="chat-close-button"><X className="w-4 h-4" /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 grid gap-3 content-start" data-testid="chat-messages">
            <Bubble m={{ role: "assistant", content: "Hi! 👋 How can we help with your cleaning today?" }} />
            {msgs.length === 0 && <div className="flex flex-wrap gap-2" data-testid="chat-suggestions">{SUGGEST.map((s) => <button key={s} type="button" onClick={() => send(s)} className="rounded-full border border-[#E5E2DA] bg-white px-3 py-1.5 text-xs hover:border-[var(--kc-blue)] transition-colors" data-testid="chat-suggestion">{s}</button>)}</div>}
            {msgs.map((m, i) => <Bubble key={i} m={m} onPrefill={prefill} />)}
            <div ref={endRef} />
          </div>
          <div className="px-4 py-2 border-t border-[#E5E2DA] flex items-center gap-3 text-xs text-[#5A606A] overflow-x-auto" data-testid="chat-human-support">
            <span className="inline-flex items-center gap-1 shrink-0"><LifeBuoy className="w-3.5 h-3.5" /> Need a human?</span>
            {settings.phone && <a href={`tel:${settings.phone}`} className="inline-flex items-center gap-1 text-[var(--kc-blue)] font-medium shrink-0" data-testid="chat-call-support"><Phone className="w-3.5 h-3.5" /> Call</a>}
            {settings.email && <a href={`mailto:${settings.email}`} className="inline-flex items-center gap-1 text-[var(--kc-blue)] font-medium shrink-0" data-testid="chat-email-support"><Mail className="w-3.5 h-3.5" /> Email</a>}
            <button type="button" onClick={() => { setOpen(false); navigate("/contact"); }} className="text-[var(--kc-blue)] font-medium shrink-0" data-testid="chat-contact-support">Contact form</button>
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-[#E5E2DA] bg-white flex gap-2">
            <input value={text} onChange={(e) => setText(e.target.value)} maxLength={1200} placeholder="Type your question…" className="kc-input h-11 flex-1" data-testid="chat-input" aria-label="Message" />
            <button type="submit" disabled={busy || !text.trim()} className="btn-primary h-11 w-11 px-0" aria-label="Send" data-testid="chat-send-button">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}</button>
          </form>
        </div>
      )}
    </>
  );
}
